import { useState, useCallback, useEffect } from "react";
import { getCookie, setCookie, deleteCookie } from "../utils/cookies";
import { isLocal } from "@/helpers/cookie.helper";
import {
  COOKIE_CHANGED_EVENT,
  type CookieChangedDetail,
} from "@/utils/cookieEvents";

type CookieUpdater<T> = (newValue: T) => void;
type CookieRemover = () => void;

export const useCookie = <T = unknown>(
  cookieName: string,
  config: { sarasDomain?: boolean } = { sarasDomain: false },
  initialValue: T | null = null
): [T | null, CookieUpdater<T>, CookieRemover] => {
  const { sarasDomain = false } = config;

  const [value, setValue] = useState<T | null>(() => {
    return getCookie<T>(cookieName) ?? initialValue;
  });

  // Pick up writes made outside React — today that is the axios interceptor
  // refreshing the auth-token cookie. Without this the state stays stale and
  // consumers gated on it (useRbac) never enable. See utils/cookieEvents.ts.
  useEffect(() => {
    const onCookieChanged = (event: Event) => {
      const changed = (event as CustomEvent<CookieChangedDetail>).detail?.name;
      if (changed !== cookieName) return;
      setValue(getCookie<T>(cookieName) ?? initialValue);
    };
    window.addEventListener(COOKIE_CHANGED_EVENT, onCookieChanged);
    return () => window.removeEventListener(COOKIE_CHANGED_EVENT, onCookieChanged);
    // `initialValue` is only the fallback for a cleared cookie; callers pass a
    // literal, so it is intentionally not a dependency (it would resubscribe on
    // every render for object defaults).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieName]);

  const updateCookie = useCallback<CookieUpdater<T>>(
    (newValue) => {
      const options = {
        domain:
          isLocal ? "localhost"
          : sarasDomain ? ".sarasanalytics.com"
          : window.location.hostname,
        sameSite: sarasDomain ? "None" : "strict",
      };
      setCookie(cookieName, newValue, options);
      setValue(newValue);
    },
    [cookieName, sarasDomain]
  );

  const removeCookie = useCallback<CookieRemover>(() => {
    const options = {
      domain:
        isLocal ? "localhost"
        : sarasDomain ? ".sarasanalytics.com"
        : window.location.hostname,
      sameSite: sarasDomain ? "None" : "strict",
    };
    deleteCookie(cookieName, options);
    setValue(null);
  }, [cookieName, sarasDomain]);

  return [value, updateCookie, removeCookie];
};
